import React, { FormEvent, useState } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { Box, Collapse, Dialog, DialogContent, FormHelperText, FormLabel, Grid } from '@material-ui/core'

import checkIcon from '../assets/check.svg'

import { IOSSwitch } from './IOSSwitch'
import { ButtonLoader } from './ButtonLoader'
import { useMatchProductsButtonStyles } from './MatchProductsButton'
import { MatchMsProductPropSelector } from './MatchMsProductPropSelector'
import { MatchStoreProductPropSelector } from './MatchStoreProductPropSelector'

import { useMessages } from '../hooks/snackbarHooks'
import { useBoolState, useToggleState } from '../hooks/commonHooks'

import { EnumConstraint } from '../common/tsUtils'

import { integrationTypeNames } from '../types/integrationTypeUtils'
import { AccountProps, StoreAccountModel } from '../types/accountProps'
import { SetAutoMatchNewStoreProductsModel } from '../server/mpsklad_core/Models/SetAutoMatchNewStoreProductsModel'

export type AutoMatchNewStoreProductsButtonProps<
  TAccount extends StoreAccountModel,
  TStoreProp extends number & EnumConstraint<TStoreProp>
> =
  AccountProps<TAccount>
  & {
  storeProps: ReadonlyArray<readonly [TStoreProp, string]>

  initialStoreProp: TStoreProp

  submit: (model: SetAutoMatchNewStoreProductsModel, storeProp: TStoreProp) => Promise<void>
}

export const AutoMatchNewStoreProductsButton =
  observer(
    <TAccount extends StoreAccountModel, TStoreProp extends number & EnumConstraint<TStoreProp>>
    ({account, initialStoreProp, storeProps, submit}: AutoMatchNewStoreProductsButtonProps<TAccount, TStoreProp>) => {
      const classes = useMatchProductsButtonStyles()

      const {showSuccess} = useMessages()

      const [isModalOpen, openModal, closeModal] = useBoolState()
      const [isSubmitting, setSubmitting, setSubmitted] = useBoolState()

      const [isAutoMatchEnabled, toggleAutoMatchEnabled] = useToggleState(account.autoMatchNewProducts)

      const [storeProp, setStoreProp] = useState(initialStoreProp)
      const [msProp, setMsProp] = useState(account.autoMatchMsProp)

      const onSubmit =
        async (e: FormEvent) => {
          e.preventDefault()
          setSubmitting()

          try {
            await submit({accountId: account.id, isEnabled: isAutoMatchEnabled, msProp: msProp}, storeProp)
            showSuccess(`Автоматчинг ${(isAutoMatchEnabled ? 'включен' : 'отключен')}!`)
            closeModal()
          } finally {
            setSubmitted()
          }
        }

      return <>
        <button
          onClick={openModal}
          className={clsx('btn', isModalOpen && 'btn_isactive', isAutoMatchEnabled && classes.autoMatchButtonEnabled)}
        >
          <p>Автоматчинг</p>
          {account.autoMatchNewProducts && <img src={checkIcon} alt="check"/>}
        </button>

        <Dialog open={isModalOpen} onClose={closeModal} classes={{paper: classes.dialogPaper}}>
          <Box className={classes.title}>
            Автоматчинг
          </Box>

          <DialogContent>
            <Box paddingY={1}>
              <form onSubmit={onSubmit}>
                <Grid container className={classes.settingsTopic}>
                  <Grid item xs={12}>
                    <Box width={'100%'} display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <FormLabel className={classes.label}>
                          Автоматическое сопоставление новых товаров
                        </FormLabel>

                        <FormHelperText className={classes.helperText}>
                          При появлении новых товаров в МоёмСкладе или {integrationTypeNames[account.integrationType]},
                          они будут автоматически сопоставлены по выбранным полям.
                        </FormHelperText>
                      </Box>

                      <IOSSwitch checked={isAutoMatchEnabled} onChange={toggleAutoMatchEnabled}/>
                    </Box>
                  </Grid>
                </Grid>

                <Collapse in={isAutoMatchEnabled}>
                  <Box marginTop={3}>
                    <MatchStoreProductPropSelector
                      integrationType={account.integrationType}
                      storeProp={storeProp}
                      storeProps={storeProps}
                      onChange={setStoreProp}
                    />
                  </Box>

                  <Box marginTop={3}>
                    <MatchMsProductPropSelector msProp={msProp} onChange={setMsProp}/>
                  </Box>
                </Collapse>

                <Box marginTop={3} className={classes.actions}>
                  <button
                    type="button"
                    className={clsx(classes.cancelButton, 'default-button')}
                    onClick={closeModal}
                    disabled={isSubmitting}
                  >
                    <p>Отменить</p>
                  </button>

                  <button
                    type="submit"
                    className={clsx(classes.saveButton, 'default-button')}
                    disabled={isSubmitting}
                  >
                    <p>
                      {
                        isSubmitting
                        ? <ButtonLoader text="Сохраняем..."/>
                        : 'Сохранить'
                      }
                    </p>
                  </button>
                </Box>
              </form>
            </Box>
          </DialogContent>
        </Dialog>
      </>
    })